import type { FeatureFlagSet } from "../lib/featureFlags/types";

export const UNIZO_FEATURE_FLAGS = {
  continueMeeting: {
    label: "商談の続きを記録",
    description: "グループ内の前回商談から企業・担当者を引き継いで記録開始",
    defaultValue: false,
  },
  pushNotifications: {
    label: "Push通知(Chrome)",
    description:
      "Chromeブラウザへのweb push通知を有効化。設定画面にPushバナーと許可ボタンを表示",
    defaultValue: false,
  },
  meetingNlSearch: {
    label: "AIエージェント検索 (Stage 1)",
    description: "自然言語で商談を検索する API エンドポイントを有効化（メタデータフィルタ）",
    defaultValue: false,
  },
  meetingNlSearchVector: {
    label: "AIエージェント検索: ベクトル検索 (Stage 3)",
    description: "S3 Vectors による意味検索を有効化（meetingNlSearch との併用）",
    defaultValue: false,
  },
  meetingNlSearchRerank: {
    label: "AIエージェント検索: LLM rerank (Stage 3)",
    description: "検索結果を LLM で再ランキング（meetingNlSearch との併用）",
    defaultValue: false,
  },
  companyExternalSystemId: {
    label: "全店顧客番号入力（商談編集画面）",
    description:
      "商談編集画面に「全店顧客番号」(企業マスタ systemIntegrationId) の入力欄を表示し、保存時に企業マスタへ反映する。法人商談は取引先名の下、個人商談はお客様情報の下 (PRODUCT_DEV-952)。基幹システム側の顧客番号を商談に紐づけて運用するテナント向け。OFFで非表示・既存挙動のまま (PRODUCT_DEV-557)。",
    defaultValue: false,
  },
  aiVoiceVolumeSlider: {
    label: "AI音声 音量スライダー",
    description:
      "録音画面に AI 音声出力の音量スライダーを表示 (全 OS)。再生経路は iOS/iPadOS のみ Web Audio(GainNode) で音量制御し (iOS Safari は audio.volume を黙殺するため PRODUCT_DEV-556)、Android/PC は従来の audio.volume 経路 (PRODUCT_DEV-605: Android で AudioContext がメディア音量に固定され側面ボタンの通話音量と食い違うデグレを修正)。OFF は全 OS で従来経路のまま (PRODUCT_DEV-541)。",
    defaultValue: false,
  },
  guideRenewal: {
    label: "使い方ガイド（新版）",
    description:
      "ONのテナントは刷新後の使い方ガイドを表示する。OFFは現行ガイド（回帰なし）。自社テナント先行検証→100%展開用 (PRODUCT_DEV-560)。",
    defaultValue: false,
  },
  sansanContactRegistration: {
    label: "Sansanコンタクト連携",
    description:
      "商談詳細に「Sansanへ登録」ボタンを表示し、商談をSansanのコンタクト(接点記録)として登録するAPI連携を有効化。自社テナント先行検証(PoC)→将来テナント別展開 (PRODUCT_DEV-531)。",
    defaultValue: false,
  },
  sansanExistingCardMatch: {
    label: "Sansan連携: 既存の名刺にそろえて送る",
    description:
      "Sansanへ登録する確認モーダルで、社外の担当者ごとに Sansan の既存の名刺の候補を出し、選んだ名刺の会社名・氏名・メールアドレスをそのまま送る（Sansan が同じ人・会社にまとめる）。選ばなかった担当者にもメールアドレスを足して送る。「Sansanコンタクト連携」が ON のテナントでだけ効く。OFF は今までどおり（回帰なし）(PRODUCT_DEV-1143)。",
    defaultValue: false,
  },
  inquiryQa: {
    label: "問い合わせ前のAI Q&A",
    description:
      "お問い合わせ一覧の主導線を「質問する」に変え、問い合わせフォームの手前にAI Q&Aを挟む。使い方ガイドを根拠にLLMがその場で回答し、解決しない場合は質問とAI回答を引き継いで従来のフォームへ進む。回答は運営の承認を経ずユーザへ直接届く(既存の問い合わせAIドラフトとはここが異なる)。不具合報告・要望などQ&Aが噛み合わない用件のために「運営に直接問い合わせる」副導線を併置する。OFFは現行どおり「新しく問い合わせる」から直接フォームへ、/inquiries/qa も遮断（回帰なし） (PRODUCT_DEV-690)。",
    defaultValue: false,
  },
  inquiryFaq: {
    label: "問い合わせのよくある質問(FAQ)",
    description:
      "お問い合わせに「よくある質問」の一覧を出し、AI Q&A と運営向けの AI 回答下書きが公開中の FAQ もガイドと並べて根拠にする（回答の参照先に FAQ が出る）。FAQ は全テナント共通の1セットで、運営(Umeee)が Admin で登録する。使い方ガイドの「よくある質問」もこの一覧へ案内する（ガイド側の Q&A は FAQ へ移した）。OFF は一覧を出さず GET /inquiries/faqs も 403、AI Q&A と AI 下書きにも FAQ を渡さない（回帰なし） (PRODUCT_DEV-908)。",
    defaultValue: true,
  },
  scheduling: {
    label: "日程調整",
    description:
      "マイページに日程調整を出す。候補日時を作って相手へワンタイムリンクを送り、相手はログインせずに候補ごとの ○△× で答える。確定・日時変更・取り消しはメールと .ics で相手に届く。OFF のときはマイページの行が「準備中」表示のままで、/mypage/scheduling も遮断する（回帰なし）。**相手の回答画面 /s/:token はフラグで塞がない** — 相手のテナントは判別できず、リンクが死ぬ方が害が大きいため (PRODUCT_DEV-675)。",
    defaultValue: false,
  },
  schedulingCalendarSync: {
    label: "外部カレンダー連携（Google / Microsoft 365）",
    description:
      "利用者が自分の Google カレンダー / Microsoft 365 の予定表を1つだけつなぎ、その予定をマイ手帳の時間割に出す（読むだけ・予定は保存せず開くたびに読む・本人にしか見えない）。ON のテナントだけ、設定画面に「カレンダーをつなぐ／外す／つなぎ替える」と、管理者向けに自社の Google アプリを登録する欄が出る。⚠ **手帳に出すのは `diaryHome` も ON のときだけ**（手帳が無いテナントでは、つないでも出す場所が無い）。⚠ 日程調整の空き時間の判定にはまだ使っていない（日程調整の「つなぐと候補日時が正確になります」の案内カードは、判定を作るまで出さない）。OFF は現行どおり（連携の入口も API も出さない）で回帰なし (PRODUCT_DEV-675 で置き場所だけ作り、PRODUCT_DEV-1225 で中身を入れた)。",
    defaultValue: false,
  },
  minutesMeetingLink: {
    label: "議事録と商談記録の紐づけ",
    description:
      "商談詳細に「関連議事録」(タイトル/日付/AI要約)を表示し、編集画面から追加・解除できるようにする。あわせて議事録詳細に「商談の振り返りを記録」を追加し、議事録要約を差し込んだ対話で当日の顧客の反応(声色・仕草・表情など)を聞き出して商談記録として保存する。振り返りのプロンプトは Admin ONP「振り返り記録プロンプト」タブで管理(フリーテキスト)。要約は商談詳細でテナント全員に見えるが、議事録本体・全文の閲覧範囲は従来どおり本人＋管理者のみ。OFFは現行どおり議事録詳細から片方向に紐づけるのみ（回帰なし） (PRODUCT_DEV-612)。",
    defaultValue: false,
  },
  csMeetingNotification: {
    label: "商談記録の運営 Slack 通知",
    description:
      "このテナントで商談記録が作成されるたび、運営(カスタマーサクセス)の Slack チャンネルへ商談タイトル・プリセット名・記録モード・商談詳細リンクを送信する。商談タイトルには顧客名や個人名が含まれうるため、テナントの合意が取れている場合にのみ ON にすること。テナント向けの通知 Webhook (PRODUCT_DEV-379) とは別系統で、キーワード判定はせず全件が対象。OFFで送信しない (PRODUCT_DEV-495 / PRODUCT_DEV-735)。",
    defaultValue: false,
  },
  managerRole: {
    label: "権限の2軸分離（マネージャー）",
    description:
      "権限を「システム軸（sysadmin / admin）＝設定とデータを管理する人」と「業務軸（manager / member）＝部署のメンバーを見る人」の2軸に分ける。ONのテナントでは、部のまとめ・部の音声・部下の日報・日報へのコメント・部のまとめメールの宛先が **その部署の manager** に移り、admin だけでは業務の数字を見られなくなる（部署を1つも作っていないテナントの未所属枠だけは admin に残す。所属レコードが無いと manager を置けないため）。あわせてユーザー管理・パスワード再設定・データ出力を sysadmin から admin へ降ろす（横断で見たいときの正規ルートをデータ出力にするため）。OFFは現行どおり admin 基準で、ユーザー管理とデータ出力も sysadmin 限定のまま（回帰なし）。⚠ 兼務できるデータ形（所属レコードの複数持ち）はフラグに関係なく入るが、OFFのあいだ画面は従来どおり1人1部署として振る舞う。⚠ 特殊権限(specialFlags)の付与が sysadmin 限定である点はフラグに関係なく常時有効（RULES Rule5） (PRODUCT_DEV-721)。",
    defaultValue: false,
  },
  diaryHome: {
    label: "手帳（カレンダー起点のホーム）",
    description:
      "マイページ TOP を「手帳」に建て替え、アプリを開いて最初に出る画面にする。1日1ページの紙の手帳と同じで、その日の予定・ToDo・メモ・日程調整が同じページに綴じられ、月カレンダーで日を送る。あわせて下のタブを「手帳 / 商談記録 / 顧客一覧 / メニュー」の4つに差し替え、議事録詳細・商談詳細に「議事録 → 商談記録 → 振り返り」の帯を出す（⚠ **帯だけは `minutesMeetingLink` にも依存する** — 紐づけ機能が OFF のテナントでは、つなぐ相手が無いので帯も出さない）。⚠ **商談記録一覧の URL（`/`）は動かさない** — 絞り込みを URL に載せる作りで、通知・共有リンク・ブックマークがこの URL を指しているため。ホームの入れ替えはログイン後の着地先とタブの並びで行う。OFF は現行どおり（ホームは商談記録一覧、`/mypage` は URL 直打ちのみ、タブは5つ、帯は出ない）で回帰なし (PRODUCT_DEV-782)。",
    defaultValue: false,
  },
  meetingTodos: {
    label: "商談記録のToDo（AI のToDo候補）",
    description:
      "商談詳細に「ToDo候補」と「この商談のToDo」を出す。初めて編集画面を開いたときに AI が商談記録・文字起こし・紐づく公開の議事録からToDo候補を作り（以後は保存済みを出し、「再生成」で作り直せる）、「＋ ToDo」で記録者のToDoとして手帳に入る。AI（gpt-5.4-mini）を商談ごとにおおむね1回呼ぶ（1回あたり約2〜3円）。OFF は商談詳細に欄を出さず、商談のToDo API（/meetings/{id}/suggest-todos・/meetings/{id}/todos）も 403 にする（AI も呼ばない）。OFF にしても、すでに作ったToDoは手帳に残る (PRODUCT_DEV-998)。",
    defaultValue: false,
  },
  onboarding: {
    label: "はじめての方へ（初回オンボーディング）",
    description:
      "はじめて使う人向けの体験型の案内を出す。ようこそ → STEP 1 商談記録（記録作成の練習・下書きの確認・本登録・振り返り・商談一覧）→ STEP 2 マイ手帳（日のめくり方・月カレンダー・予定・日程調整・ToDo・日報・メモ）→ STEP 3 便利な機能（商談一覧・顧客一覧・辞書のぷち体験）の3段で、本物の画面に決まった筋書きと見本のデータを入れて指で進める（録音・API 呼び出し・データの保存はしない。練習の記録は残らない）。商談記録・議事録がまだ1件も無い人にだけ、アプリを開いたときに1度自動で出し、ほかの人はメニューの「はじめての方へ」から開く。メニューの行と下のメニューの「メニュー」タブに、1度開くまで赤ポチを出す。進み具合は本人の個人設定（ONBOARDING）に保存し、端末をまたいで続きから開ける。⚠ **`diaryHome` も ON のときだけ出す**（STEP 2 がマイ手帳の案内のため）。OFF は何も出さない（回帰なし） (PRODUCT_DEV-783)。",
    defaultValue: false,
  },
  callInterruptionPause: {
    label: "録音中の着信で一時停止",
    description:
      "対話録音・議事録録音の途中で電話がかかってきたとき（マイクの無音化・切断、音声の割り込み）、自動で一時停止して通話の区間は送らず、経過時間も止める。戻ったら画面の「再開」で続きから録れ（マイクが切れていたら取り直す）、「ここで保存」でそこまでを保存できる。Android は振動でも知らせる（iPhone の Safari は Web から振動させられないので画面だけ）。対話録音は再開すると、途中だった質問から前置きなしで聞き直す。議事録録音は一時停止中、60分までは確定を待つ。あわせて、中断のあと保存や終了で止まらないよう待ちに上限を付ける。OFF は現行どおり（議事録録音はマイクが切れると自動で確定、対話録音はそのまま続く）で回帰なし (PRODUCT_DEV-1087)。",
    defaultValue: false,
  },
  caseFolder: {
    label: "案件フォルダ（商談グループの改善）",
    description:
      "商談グループの呼び名を「案件フォルダ」にし、顧客の中で続いている話を入れておく場所として使えるようにする。①フォルダ詳細に要約をそのまま出し（最終更新の日時と再生成つき）、「この案件の続きを記録する」から記録を始められる（始めた記録はそのフォルダに入る。AI が要約を踏まえて対話するのは `continueMeeting` も ON のときだけ）。商談は顧客詳細と同じ数珠つなぎで並べる。②フォルダに相手の顧客を任意で持たせる（商談一覧の「まとめる」で選んだ商談が全部同じ顧客のときだけ自動で付ける。名前での推測はしない。フォルダ詳細の右上から後で選べる）。顧客詳細に「この顧客の案件フォルダ」と「案件フォルダを作る」を出す。③本登録した商談記録の中に任意の1行「どの案件の続きですか？」を出す（押さなければ何も起きない）。④次回アポの予定を押したとき、その顧客の案件フォルダの要約を「前回のおさらい」として出す（作ってある要約を出すだけで AI は新しく呼ばない）。商談一覧の「まとめる」は1件からできる。OFF は現行どおり（呼び名は「グループ」・まとめるは2件から）で回帰なし (PRODUCT_DEV-1214)。",
    defaultValue: false,
  },
  customerLookupUnizoWording: {
    label: "お客さまを確かめるときの言い方（Unizo の登録）",
    description:
      "録音中の AI がお客さま・企業を探す前の一言を「顧客マスタを確認しますね。」から「ユニゾーの登録を確認しますね。」に、法人で見つからなかったときの一言を「顧客マスタに登録がないので、仮登録しておきます」から「ユニゾーにまだ登録がないので、新しく登録しておきます」に変える（読みを決めるため話す文はカタカナ）。行内の CRM を見に行っているように聞こえないため。ONP の上書き（COMPANY_SEARCH / INDIVIDUAL_COMPANY_SEARCH）があってもその文の中の言い方を差し替える。OFF は現行どおりで回帰なし (PRODUCT_DEV-1234)。",
    defaultValue: false,
  },
  individualCandidateSystemId: {
    label: "候補に全店顧客番号を表示（商談編集画面）",
    description:
      "商談編集画面の「どなたの記録ですか？」の候補に、全店顧客番号（企業マスタ systemIntegrationId）を添える。同じ名前のお客さまを番号で見分けるため。会話では番号を聞かない。⚠ **`companyExternalSystemId`（全店顧客番号入力）も ON のときだけ効く**。番号が登録されていないお客さまには何も出ない。OFF は候補 API が番号を返さず、画面も現行どおりで回帰なし (PRODUCT_DEV-1234)。",
    defaultValue: false,
  },
} as const satisfies FeatureFlagSet;

export type UnizoFeatureFlagKey = keyof typeof UNIZO_FEATURE_FLAGS;
